/**
 * Named easing curves — OPTIONAL starter tokens, not a house style.
 *
 * These are conveniences a game MAY copy from or ignore. They are NOT a shared
 * "product feel" to conform to: each game invents its own motion. (An early
 * version of this project shared an aesthetic base here and we moved away from
 * it — different games need different feels.) What IS shared across games is
 * mechanical only: the audio context + mute gate, image loading, haptics, and
 * reduced-motion. If you want a curve this list doesn't have, define it where
 * the feel lives — your game's `motion.ts` or render loop.
 *
 * Two forms exposed per curve:
 *   - `*.css`   — `cubic-bezier(...)` string for CSS `transition-timing-function`
 *   - `*.tuple` — `[x1, y1, x2, y2]` array for motion.dev's `ease` option
 *
 * The set biases toward "out" curves — calm and unhurried, which suits a
 * 70–95+ audience better than snappy "in-out" curves. A game is free to
 * disagree.
 */

export const easing = {
  /** Default ease. Calm, dependable. Use this when in doubt. */
  outCubic: {
    css: 'cubic-bezier(0.33, 1, 0.68, 1)',
    tuple: [0.33, 1, 0.68, 1] as const,
  },
  /** Like outCubic but more pronounced. Use for entrances. */
  outQuart: {
    css: 'cubic-bezier(0.25, 1, 0.5, 1)',
    tuple: [0.25, 1, 0.5, 1] as const,
  },
  /**
   * Overshoots a touch on the way out. Use sparingly — celebrations,
   * answer reveals, "ta-da" moments. Never on input affordances.
   */
  outBack: {
    css: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
    tuple: [0.34, 1.56, 0.64, 1] as const,
  },
  /** Tight ease for press/release feedback on buttons. */
  inOutQuad: {
    css: 'cubic-bezier(0.45, 0, 0.55, 1)',
    tuple: [0.45, 0, 0.55, 1] as const,
  },
  /** For exits. Eases in (slow start, faster finish). */
  inCubic: {
    css: 'cubic-bezier(0.32, 0, 0.67, 0)',
    tuple: [0.32, 0, 0.67, 0] as const,
  },
} as const;

export type EasingName = keyof typeof easing;
